import React from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useSidebarStore } from "../../store/useSidebarStore";
import { useDeviceDetect } from "../../hooks/useDeviceDetect";
import { useAuthStore } from "../../store/useAuthStore";
import { useSportsLaunch } from "../../hooks/useSportsLaunch";
import { Casino, Game, SlotsSkyla, CrashIcon, PromotionIcon, Vip, BonusIcon, AffliateIcon, Provably } from "../icons";

const topNav = [
  { path: "/casino", label: "Casino", Icon: Casino },
  { path: null, label: "Sports", Icon: Game, isSports: true },
  { path: "/slots", label: "Slots", Icon: SlotsSkyla },
  { path: "/live-casino", label: "Live", Icon: CrashIcon },
];

const bottomNavAll = [
  { path: "/promotions", label: "Promotions", Icon: PromotionIcon },
  { path: "/vip-club", label: "VIP Club", Icon: Vip, special: true },
  { path: "/bonus", label: "Bonus", Icon: BonusIcon, authOnly: true },
  { path: "/affiliate", label: "Affiliate", Icon: AffliateIcon },
  { path: "/provably-fair", label: "Provably Fair", Icon: Provably },
];

const Sidebar = () => {
  const { isOpen, isCollapsed, close } = useSidebarStore();
  const { isMobile } = useDeviceDetect();
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const handleSportsLaunch = useSportsLaunch();
  const bottomNav = bottomNavAll.filter((item) => !item.authOnly || isAuthenticated);

  // Mobile: overlay sidebar
  if (isMobile) {
    return (
      <>
        {isOpen && (
          <div style={{ position: "fixed", inset: 0, zIndex: 998, backgroundColor: "rgba(0,0,0,0.5)" }} onClick={close} />
        )}
        <div
          className={`sidebar-container ${isOpen ? "" : ""}`}
          style={{
            position: "fixed",
            left: isOpen ? 0 : "-280px",
            top: 0,
            width: "280px",
            paddingTop: "64px",
            transition: "left 0.3s ease",
            zIndex: 999,
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-end", padding: "8px" }}>
            <button onClick={close} style={{ color: "#fff", background: "none", border: "none", cursor: "pointer" }}>
              <X size={20} />
            </button>
          </div>
          <div className="menu-section">
            <SidebarItems items={topNav} location={location} showLabels={true} onSportsClick={handleSportsLaunch} />
            <SidebarItems items={bottomNav} location={location} showLabels={true} isBottom={true} />
          </div>
        </div>
      </>
    );
  }

  // Desktop: absolute positioned sidebar
  return (
    <div className={`sidebar-container ${isCollapsed ? "collapsed" : "expanded"}`}>
      <div className="menu-section">
        <SidebarItems items={topNav} location={location} showLabels={!isCollapsed} onSportsClick={handleSportsLaunch} />
        <SidebarItems items={bottomNav} location={location} showLabels={!isCollapsed} isBottom={true} />
      </div>
    </div>
  );
};

function SidebarItems({ items, location, showLabels, isBottom, onSportsClick }) {
  return (
    <>
      {items.map((item, idx) => {
        const isActive = location.pathname === item.path;
        const isFirst = isBottom && idx === 0;

        if (item.isSports) {
          return (
            <div
              key="sports"
              className="menu-item"
              style={{
                cursor: "pointer",
                marginTop: isFirst ? "20px" : undefined,
                justifyContent: showLabels ? "flex-start" : "center",
              }}
              onClick={onSportsClick}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div className="menu-icon">
                  <item.Icon size="28px" />
                </div>
                {showLabels && (
                  <span className="menu-text">{item.label}</span>
                )}
              </div>
            </div>
          );
        }

        return (
          <Link
            key={item.path}
            to={item.path}
            className={`menu-item ${isActive ? "active" : ""}`}
            style={{
              textDecoration: "none",
              marginTop: isFirst ? "20px" : undefined,
              justifyContent: showLabels ? "flex-start" : "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <div className="menu-icon">
                <item.Icon size="28px" />
              </div>
              {showLabels && (
                <span
                  className={`menu-text ${isActive ? "active" : ""} ${item.special ? "vip" : ""}`}
                >
                  {item.label}
                </span>
              )}
            </div>
          </Link>
        );
      })}
    </>
  );
}

export default Sidebar;
